
import { useAuth } from "@/contexts/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

export interface CustomPricingTableProps {
  className?: string;
  customClassName?: string;
  onPlanSelect?: (plan: string) => void;
  selectedPlanId?: string;
  currentTier?: string;
}

type PlanId = "free_tier" | "whatsapp_tier" | "premium_tier";
type FeatureValue = string | boolean;

const CustomPricingTable = ({
  className = "",
  customClassName = "",
  onPlanSelect,
  selectedPlanId,
  currentTier = 'free_tier'
}: CustomPricingTableProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('pricing');
  const combinedClassName = cn(className, customClassName);
  const isCurrentPlanForUser = (planId: PlanId) => Boolean(user && currentTier === planId);

  const isPlanDisabled = (planId: string): boolean => {
    if (currentTier === 'free_tier') {
      return false;
    }
    
    if (currentTier === 'whatsapp_tier') {
      return planId === 'whatsapp_tier' || planId === 'free_tier';
    }
    
    if (currentTier === 'premium_tier') {
      return true;
    }
    
    return false;
  };

  const handleSelectPlan = (planId: PlanId) => {
    if (isPlanDisabled(planId) || isCurrentPlanForUser(planId)) {
      return;
    }

    if (onPlanSelect) {
      onPlanSelect(planId);
      return;
    }

    if (!user) {
      navigate(`/auth?tab=signup&redirect=${encodeURIComponent(`/pricing?checkout=${planId}`)}`);
    } else {
      navigate(`/pricing?checkout=${planId}`);
    }
  };

  const getButtonText = (planId: PlanId) => {
    if (!user) {
      return t('buttons.signUp');
    }

    if (isCurrentPlanForUser(planId)) {
      return t('buttons.currentPlan');
    }

    if (isPlanDisabled(planId)) {
      return t('buttons.notAvailable');
    }

    return t('buttons.purchase');
  };

  const plans: Array<{
    id: PlanId;
    title: string;
    price: string;
    period: string;
    description: string;
    headerClassName: string;
  }> = [
    {
      id: "free_tier",
      title: t('pricingTable.free.title'),
      price: t('pricingTable.free.price'),
      period: t('pricingTable.free.period'),
      description: t('pricingTable.free.description'),
      headerClassName: "bg-white",
    },
    {
      id: "whatsapp_tier",
      title: t('pricingTable.whatsapp.title'),
      price: t('pricingTable.whatsapp.price'),
      period: t('pricingTable.whatsapp.period'),
      description: t('pricingTable.whatsapp.description'),
      headerClassName: "bg-whatsapp-light",
    },
    {
      id: "premium_tier",
      title: t('pricingTable.premium.title'),
      price: t('pricingTable.premium.price'),
      period: t('pricingTable.premium.period'),
      description: t('pricingTable.premium.description'),
      headerClassName: "bg-amber-soft",
    },
  ];

  const featureRows: Array<{
    key: string;
    label: string;
    values: Record<PlanId, FeatureValue>;
  }> = [
    {
      key: "listings",
      label: t('comparison.features.listings'),
      values: {
        free_tier: t('pricingTable.free.features.maxListings'),
        whatsapp_tier: t('pricingTable.whatsapp.features.maxListings'),
        premium_tier: t('pricingTable.premium.features.unlimitedListings'),
      },
    },
    {
      key: "categories",
      label: t('comparison.features.categories'),
      values: {
        free_tier: t('pricingTable.free.features.singleCategory'),
        whatsapp_tier: t('pricingTable.whatsapp.features.singleCategory'),
        premium_tier: t('pricingTable.premium.features.multipleCategories'),
      },
    },
    {
      key: "areas",
      label: t('comparison.features.areas'),
      values: {
        free_tier: t('pricingTable.free.features.maxAreas'),
        whatsapp_tier: t('pricingTable.whatsapp.features.maxAreas'),
        premium_tier: t('comparison.values.allServiceAreas'),
      },
    },
    {
      key: "profile",
      label: t('comparison.features.profile'),
      values: {
        free_tier: t('pricingTable.free.features.basicProfile'),
        whatsapp_tier: t('pricingTable.whatsapp.features.basicProfile'),
        premium_tier: t('pricingTable.premium.features.enhancedProfile'),
      },
    },
    {
      key: "verified",
      label: t('comparison.features.verifiedBadge'),
      values: {
        free_tier: false,
        whatsapp_tier: true,
        premium_tier: true,
      },
    },
    {
      key: "analytics",
      label: t('comparison.features.analytics'),
      values: {
        free_tier: false,
        whatsapp_tier: true,
        premium_tier: true,
      },
    },
    {
      key: "priority",
      label: t('comparison.features.priority'),
      values: {
        free_tier: false,
        whatsapp_tier: false,
        premium_tier: true,
      },
    },
  ];

  const renderFeatureValue = (value: FeatureValue, showLabel = false) => {
    if (typeof value === "boolean") {
      const label = value ? t('comparison.included') : t('comparison.notIncluded');
      const Icon = value ? Check : Minus;

      return (
        <span
          className={cn(
            "inline-flex items-center justify-center gap-2",
            value ? "text-brand-ink" : "text-brand-slate"
          )}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          <span className={showLabel ? "" : "sr-only"}>{label}</span>
        </span>
      );
    }

    return <span>{value}</span>;
  };

  return (
    <div className={cn("w-full", combinedClassName)}>
      <div className="hidden overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-brand-cloud hover:bg-transparent">
              <TableHead className="w-[26%] min-w-[12rem] bg-brand-mist p-6 align-bottom text-brand-ink">
                <div className="public-card-title">
                  {t('comparison.headers.features')}
                </div>
              </TableHead>
              {plans.map((plan) => {
                const isCurrentPlan = isCurrentPlanForUser(plan.id);

                return (
                  <TableHead
                    key={plan.id}
                    className={cn(
                      "h-auto min-w-[13rem] border-l border-brand-cloud p-0 text-center align-top text-brand-ink",
                      plan.headerClassName
                    )}
                  >
                    <div className="flex min-h-[240px] flex-col p-5">
                      <div className="public-card-title">
                        {plan.title}
                      </div>
                      <p className="mt-2 min-h-10 text-sm font-normal leading-5 text-brand-slate">
                        {plan.description}
                      </p>
                      <div className="mt-5 flex items-baseline justify-center gap-1 text-brand-ink">
                        <span className="font-heading text-h2 font-bold">{plan.price}</span>
                        <span className="text-sm font-normal text-brand-slate">/ {plan.period}</span>
                      </div>
                      <Button
                        className="mt-auto w-full bg-brand-ink text-white hover:bg-brand-charcoal"
                        disabled={isPlanDisabled(plan.id) || isCurrentPlan}
                        onClick={() => handleSelectPlan(plan.id)}
                      >
                        {getButtonText(plan.id)}
                      </Button>
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {featureRows.map((feature) => (
              <TableRow key={feature.key} className="border-brand-cloud hover:bg-brand-mist">
                <TableCell className="bg-brand-mist p-5 font-medium text-brand-ink">
                  {feature.label}
                </TableCell>
                {plans.map((plan) => (
                  <TableCell
                    key={`${feature.key}-${plan.id}`}
                    className="border-l border-brand-cloud p-5 text-center text-sm text-brand-slate"
                  >
                    {renderFeatureValue(feature.values[plan.id])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-4 md:hidden">
        {plans.map((plan) => {
          const isCurrentPlan = isCurrentPlanForUser(plan.id);

          return (
            <section
              key={plan.id}
              className="overflow-hidden rounded-md border border-brand-cloud bg-white shadow-sm"
              aria-label={plan.title}
            >
              <div className={cn("p-5", plan.headerClassName)}>
                <h3 className="public-card-title">{plan.title}</h3>
                <p className="mt-2 text-sm leading-5 text-brand-slate">{plan.description}</p>
                <div className="mt-4 flex items-baseline gap-1 text-brand-ink">
                  <span className="font-heading text-h2 font-bold">{plan.price}</span>
                  <span className="text-sm text-brand-slate">/ {plan.period}</span>
                </div>
                <Button
                  className="mt-5 w-full bg-brand-ink text-white hover:bg-brand-charcoal"
                  disabled={isPlanDisabled(plan.id) || isCurrentPlan}
                  onClick={() => handleSelectPlan(plan.id)}
                >
                  {getButtonText(plan.id)}
                </Button>
              </div>

              <dl className="divide-y divide-brand-cloud">
                {featureRows.map((feature) => (
                  <div key={feature.key} className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)] gap-3 p-4">
                    <dt className="text-sm font-medium text-brand-ink">{feature.label}</dt>
                    <dd className="text-right text-sm text-brand-slate">
                      {renderFeatureValue(feature.values[plan.id], true)}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default CustomPricingTable;
